/**
 * DashboardDetail - View a saved dashboard with its widgets
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, CircularProgress, IconButton, Tooltip, Switch, FormControlLabel } from '@mui/material';
import { ArrowLeft, Share2, Settings } from 'lucide-react';
import {} from '@diggens/shared-hooks';
import { useCurrentUser } from '@diggens/user-management';
import { authenticatedFetch } from '@diggens/api-client';
import { Responsive, WidthProvider, type Layout } from 'react-grid-layout';
import { WidgetRenderer, type Widget } from '.';
import 'react-grid-layout/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface Dashboard {
  id: string;
  name: string;
  description: string | null;
  is_shared: boolean;
  created_by: string;
  module: string | null;
}

export function DashboardDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (!id) return;

    const fetchDashboard = async () => {
      try {
        const response = await authenticatedFetch(`/dashboards/${id}`);
        if (response.ok) {
          const data = await response.json();
          setDashboard(data.dashboard);
          setWidgets(data.widgets || []);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [id]);

  const handleShareToggle = async () => {
    if (!dashboard || !user) return;
    // TODO: Implement share toggle API
    setDashboard({ ...dashboard, is_shared: !dashboard.is_shared });
  };

  const isOwner = dashboard?.created_by === user?.id;

  const generateLayout = useCallback((): Layout[] => {
    return widgets.map((widget) => {
      const w = widget.width ?? (widget.chart_type === 'kpi_card' ? 1 : 2);
      const h = widget.height ?? (widget.chart_type === 'kpi_card' ? 1 : 2);
      return {
        i: widget.id,
        x: widget.grid_x ?? 0,
        y: widget.grid_y ?? 0,
        w,
        h,
        minW: 1,
        minH: 1,
      };
    });
  }, [widgets]);

  const handleLayoutChange = useCallback(
    async (newLayout: Layout[]) => {
      if (!dashboard || !editMode) return;
      const updates = newLayout.map((item) => ({
        id: item.i,
        grid_x: item.x,
        grid_y: item.y,
        width: item.w,
        height: item.h,
      }));
      setWidgets((prev) =>
        prev.map((w) => {
          const update = updates.find((u) => u.id === w.id);
          if (update) {
            return {
              ...w,
              grid_x: update.grid_x,
              grid_y: update.grid_y,
              width: update.width,
              height: update.height,
            };
          }
          return w;
        })
      );
      try {
        await authenticatedFetch(`/dashboards/${dashboard.id}/widgets/layout`, {
          method: 'PATCH',
          body: JSON.stringify({ layout: updates }),
        });
      } catch (error) {
        console.error('Failed to save layout:', error);
      }
    },
    [dashboard, editMode]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!dashboard) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Dashboard not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Tooltip title="Back to Analytics">
          <IconButton onClick={() => navigate('/analytics')}>
            <ArrowLeft size={20} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4">{dashboard.name}</Typography>
          {dashboard.description && (
            <Typography variant="body2" color="text.secondary">
              {dashboard.description}
            </Typography>
          )}
        </Box>
        {isOwner && (
          <>
            <Tooltip title={editMode ? 'Exit edit mode' : 'Edit layout'}>
              <IconButton onClick={() => setEditMode(!editMode)} color={editMode ? 'primary' : 'default'}>
                <Settings size={20} />
              </IconButton>
            </Tooltip>
            <FormControlLabel
              control={<Switch checked={dashboard.is_shared} onChange={handleShareToggle} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Share2 size={16} />
                  <span>Shared</span>
                </Box>
              }
            />
          </>
        )}
      </Box>

      {widgets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No widgets yet. Add charts from the AI assistant.</Typography>
        </Paper>
      ) : (
        <ResponsiveGridLayout
          layouts={{ lg: generateLayout() }}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 4, md: 4, sm: 2, xs: 1, xxs: 1 }}
          rowHeight={150}
          isDraggable={editMode && isOwner}
          isResizable={editMode && isOwner}
          onLayoutChange={handleLayoutChange}
          draggableHandle=".widget-drag-handle"
        >
          {widgets.map((widget) => {
            const isKpi = widget.chart_type === 'kpi_card';
            return (
              <Paper key={widget.id} sx={{ p: 2, height: '100%', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {editMode && isOwner && (
                    <Box
                      className="widget-drag-handle"
                      sx={{
                        cursor: 'grab',
                        color: 'text.secondary',
                        '&:active': { cursor: 'grabbing' },
                      }}
                    >
                      ⋮⋮
                    </Box>
                  )}
                  <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
                    {widget.name}
                  </Typography>
                </Box>
                <Box sx={{ height: 'calc(100% - 32px)', width: '100%' }}>
                  <WidgetRenderer widget={widget} height={isKpi ? 80 : 280} module={dashboard.module || undefined} />
                </Box>
              </Paper>
            );
          })}
        </ResponsiveGridLayout>
      )}
    </Box>
  );
}
